import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Text } from '@Sage/hoi-poi-ui';

import SelectHoi from 'components/SelectHoi';
import Map from 'components/Map';
import GeoLocationFieldMarker from 'containers/components/Fields/GeoLocation/GeoLocationFieldMarker';
import { getFMAddressFromCoordinates, getFMAddressFromGeoCodeResults } from 'utils/map';
import { formatAddressFromGeocoder, shouldFormatAddressAsAmericanIso } from 'utils/addresses';
import { getLiteral } from 'utils/getLiteral';

const GeoLocationModal = ({
    isOpen,
    onCancel = null,
    onConfirm,
    initialAddress = '',
    countriesRaw = [],
    searchLocations,
    getPlaceDetails,
}) => {
    const mapComponentRef = useRef(null);
    const markerComponentRef = useRef(null);

    const [locationInput, setLocationInput] = useState('');
    const [defaultOptions, setDefaultOptions] = useState([]);
    const [locationValue, setLocationValue] = useState(null);
    const [positionMarker, setPositionMarker] = useState(null);
    const [addressFields, setAddressFields] = useState(null);
    const [fullAddress, setFullAddress] = useState(null);
    const [isMapPositionUpdated, setIsMapPositionUpdated] = useState(false);

    // Resetear estado cuando se abre/cierra el modal
    useEffect(() => {
        if (!isOpen) {
            setLocationInput('');
            setDefaultOptions([]);
            setLocationValue(null);
            setPositionMarker(null);
            setAddressFields(null);
            setFullAddress(null);
            setIsMapPositionUpdated(false);
            return;
        }

        setLocationValue(null);
        setIsMapPositionUpdated(false);
        setLocationInput(initialAddress || '');

        if (initialAddress && searchLocations) {
            searchLocations(initialAddress)
                .then((options) => setDefaultOptions(options))
                .catch(() => setDefaultOptions([]));
        }
    }, [isOpen, initialAddress, searchLocations]);

    const loadOptions = useCallback(
        (value) => {
            if (!searchLocations) return Promise.resolve([]);
            return searchLocations(value)
                .then((data) => {
                    return (
                        data?.Result?.map((item) => ({
                            label: item.name,
                            value: item.address,
                            placeId: item.place_id,
                        })) || []
                    );
                })
                .catch(() => []);
        },
        [searchLocations],
    );

    const onUpdateLocation = useCallback(
        ({ lat, lng, address }, firstLoad = false) => {
            getFMAddressFromCoordinates({ lat, lng }, address)
                .then((result) => {
                    const locationName = result?.locationName || result?.googleLocationName || null;
                    setFullAddress(locationName);
                    setAddressFields(locationName ? result : null);
                    setPositionMarker({ lat, lng });
                    if (!isMapPositionUpdated && !firstLoad) {
                        setIsMapPositionUpdated(true);
                    }
                })
                .catch(() => {
                    setFullAddress(null);
                    setAddressFields(null);
                });
        },
        [isMapPositionUpdated],
    );

    const onChangeLocation = useCallback(
        (item) => {
            setLocationValue(item);
            setTimeout(() => setLocationInput(item?.label || ''));
            if (!item || !getPlaceDetails) return;

            getPlaceDetails(item.placeId)
                .then((data) => {
                    const location = {
                        lat: data.Result?.FMGeoCodingResults?.[0]?.FMGeometry?.FMLocation?.FMLat,
                        lng: data.Result?.FMGeoCodingResults?.[0]?.FMGeometry?.FMLocation?.FMLng,
                    };

                    const lat = location.lat;
                    const lng = location.lng;
                    if (
                        typeof lat !== 'number' ||
                        typeof lng !== 'number' ||
                        Number.isNaN(lat + lng)
                    ) {
                        setFullAddress(null);
                        setAddressFields(null);
                        setPositionMarker(null);
                        return;
                    }

                    const mapRef = mapComponentRef.current;
                    const markerRef = markerComponentRef.current;
                    mapRef?.setCenter(location);
                    markerRef?.setMarkerPosition?.(location);

                    const result = getFMAddressFromGeoCodeResults(data);
                    const locationName = result?.locationName || result?.googleLocationName || null;
                    setFullAddress(locationName);
                    setAddressFields(locationName ? result : null);
                    setPositionMarker(location);
                    if (!isMapPositionUpdated) setIsMapPositionUpdated(true);
                })
                .catch(() => {
                    setFullAddress(null);
                    setAddressFields(null);
                    setPositionMarker(null);
                });
        },
        [isMapPositionUpdated, getPlaceDetails],
    );

    const onAutoCompleteSearch = useCallback(() => {
        const mapRef = mapComponentRef.current;
        if (!mapRef?.getCenter) return;

        const center = mapRef.getCenter();
        const markerRef = markerComponentRef.current;
        if (!markerRef?.setMarkerPosition) return;

        const position = { lat: center?.lat, lng: center?.lng };
        markerRef.setMarkerPosition(position);
        onUpdateLocation(position);
    }, [onUpdateLocation]);

    const onLoadMap = useCallback(() => {
        const mapRef = mapComponentRef.current;

        if (mapRef?.getCenter) {
            const center = mapRef.getCenter();
            const location = { lat: center?.lat, lng: center?.lng };
            setPositionMarker(location);
            onUpdateLocation(location, true);
        }
    }, [onUpdateLocation]);

    const handleConfirm = useCallback(() => {
        if (!positionMarker || !addressFields) {
            onCancel?.();
            return;
        }

        const americanFormat = shouldFormatAddressAsAmericanIso(addressFields.countryShort);

        let addressValue = addressFields.shortAddress || '';
        if (addressFields.streetNumber) {
            addressValue = formatAddressFromGeocoder(
                addressFields.shortAddress,
                addressFields.streetNumber,
                '',
                '',
                '',
                '',
                '',
                addressFields.countryShort,
                '',
            );
        }

        let addressDetailValue = '';
        if (addressFields.subpremise) {
            addressDetailValue = `#${addressFields.subpremise}`;
        } else if (addressFields.administrativeAreaLevel3 && !americanFormat) {
            addressDetailValue = addressFields.administrativeAreaLevel3;
        }

        const cityValue = addressFields.locality || '';
        const regionValue =
            addressFields.province ??
            (americanFormat ? addressFields.state || '' : addressFields.region || '');
        const postalCodeValue = addressFields.postalCode || '';

        let idCountryValue = null;
        if (addressFields.countryShort) {
            const countryShort = addressFields.countryShort.toLowerCase();
            const match = countriesRaw.find((item) => {
                return (
                    item.striso2?.toLowerCase() === countryShort ||
                    item.striso3?.toLowerCase() === countryShort
                );
            });
            if (match) idCountryValue = parseInt(match.value, 10);
        }

        onConfirm({
            address: addressValue,
            addressDetails: addressDetailValue,
            city: cityValue,
            region: regionValue,
            postalCode: postalCodeValue,
            country: addressFields.country || '',
            idCountry: idCountryValue,
        });
    }, [addressFields, countriesRaw, onCancel, onConfirm, positionMarker]);

    const mapSelectOverrides = useMemo(() => {
        return {
            'react-select': {
                filterOption: false,
                inputValue: locationInput || '',
                openMenuOnClick: true,
                defaultOptions,
                defaultMenuIsOpen: !isMapPositionUpdated && !!locationInput,
                onInputChange: (text, { action }) => {
                    if (
                        action === 'set-value' ||
                        action === 'input-blur' ||
                        action === 'menu-close'
                    ) {
                        return;
                    }
                    setLocationInput(text);
                },
            },
        };
    }, [defaultOptions, isMapPositionUpdated, locationInput]);

    return (
        <Modal
            className="marketing-sender-form-modal__map-modal"
            title={getLiteral('label_set_geolocation')}
            isOpen={isOpen}
            onRequestClose={onCancel}
            onCancel={onCancel}
            onConfirm={handleConfirm}
            cancelText={getLiteral('action_cancel')}
            confirmText={getLiteral('action_save')}
            isConfirmDisabled={!positionMarker}
            size="large"
            useCornerClose
        >
            <div className="marketing-sender-form-modal__map-content">
                <SelectHoi
                    placeholder={getLiteral('label_location')}
                    isClearable
                    value={locationValue}
                    loadOptions={loadOptions}
                    onChange={onChangeLocation}
                    overrides={mapSelectOverrides}
                    isFuzzy
                    className="marketing-sender-form-modal__map-select"
                    isFullWidth
                />
                <div className="marketing-sender-form-modal__map-canvas">
                    <Map
                        ref={mapComponentRef}
                        onLoadMap={onLoadMap}
                        center={positionMarker}
                        zoom={17}
                        onAutoCompleteSearch={onAutoCompleteSearch}
                    >
                        <GeoLocationFieldMarker
                            ref={markerComponentRef}
                            position={positionMarker}
                            updateLocation={onUpdateLocation}
                        />
                    </Map>
                    {fullAddress && (
                        <Text
                            as="div"
                            className="marketing-sender-form-modal__map-address"
                            type="caption"
                        >
                            {fullAddress}
                        </Text>
                    )}
                </div>
            </div>
        </Modal>
    );
};

GeoLocationModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func.isRequired,
    initialAddress: PropTypes.string,
    countriesRaw: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            label: PropTypes.string,
            striso2: PropTypes.string,
            striso3: PropTypes.string,
        }),
    ),
    searchLocations: PropTypes.func.isRequired,
    getPlaceDetails: PropTypes.func.isRequired,
};

export default memo(GeoLocationModal);
